import { useCallback, useRef, useState } from "react";

import {
  Pause as PauseIcon,
  Play as PlayIcon,
} from "@hl/base-components/lib/assets/icons.generated";
import { Video } from "@hl/base-components/lib/video";
import { PageSupportedMediaType } from "@hl/shared-features/lib/apollo/graphql.generated";
import { GenArtUrlIFrame } from "@hl/shared-features/lib/features/gen-art/GenArtIFrame";
import { ResizedImage } from "@hl/shared-features/lib/features/image";
import { Center, Grid, MediaQuery } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import WaveSurfer from "wavesurfer.js";

import { ImageFullscreenModal } from "~components/ImageFullscreenModal";
import useMintState from "~hooks/useMintState";

import * as styles from "./styles";

export const EditionHeader = () => {
  const [audioPlaying, setAudioPlaying] = useState<boolean>(false);
  /* eslint-disable  @typescript-eslint/no-explicit-any */
  const waveformRef = useRef<any>(null);
  const [imageExpanded, { open: expandImage, close: closeImageExpanded }] =
    useDisclosure(false);
  const { collection, tokens, animationUrl, contentTypeInfo } = useMintState();

  const upscaleImage = collection?.flagVariations.upscaleMintPageImage;

  const onAudioButtonClick = () => {
    if (audioPlaying) {
      waveformRef.current.pause();
    } else {
      waveformRef.current.play();
    }

    setAudioPlaying(!audioPlaying);
  };

  const setWaveformRef = useCallback(() => {
    if (
      animationUrl &&
      contentTypeInfo?.type === PageSupportedMediaType.AUDIO &&
      !waveformRef.current
    ) {
      waveformRef.current = WaveSurfer.create({
        container: "#waveform",
        height: 280,
        fillParent: true,
        barWidth: 3,
        hideScrollbar: true,
      });

      waveformRef.current.load(animationUrl);
    }
  }, [animationUrl, contentTypeInfo]);

  if (imageExpanded) {
    return (
      <ImageFullscreenModal
        imageUrl={tokens[0].image ?? ""}
        close={closeImageExpanded}
        shloms404UI={collection?.flagVariations.shloms404UI}
      />
    );
  }

  return (
    <Center>
      {animationUrl ? (
        <>
          {contentTypeInfo?.type === PageSupportedMediaType.VIDEO && (
            <Video css={styles.video} autoPlay loop muted playsInline>
              <source
                src={animationUrl}
                type={`${contentTypeInfo.type.toLowerCase()}/${contentTypeInfo.mime?.toLowerCase()}`}
              />
            </Video>
          )}
          {contentTypeInfo?.type === PageSupportedMediaType.AUDIO && (
            <div css={styles.audio}>
              <Grid>
                <Grid.Col span={3} style={{ position: "relative" }}>
                  <ResizedImage
                    src={tokens[0].image ?? ""}
                    fit="scale-down"
                    height={280}
                    width={280}
                    style={{
                      position: "absolute",
                      filter: "blur(0)",
                      zIndex: 1,
                      marginRight: 100,
                    }}
                  />
                  <button
                    onClick={onAudioButtonClick}
                    color="white"
                    style={{
                      zIndex: 10,
                      borderRadius: "100%",
                      border: "none",
                      width: 70,
                      height: 70,
                      position: "absolute",
                      top: 105,
                      left: 105,
                    }}
                  >
                    {audioPlaying ? (
                      <PauseIcon css={styles.buttonIcon} />
                    ) : (
                      <PlayIcon css={styles.buttonIcon} />
                    )}
                  </button>
                </Grid.Col>
                <Grid.Col span={8} offset={1}>
                  <div id="waveform" ref={setWaveformRef}></div>
                </Grid.Col>
              </Grid>
            </div>
          )}
          {contentTypeInfo?.isHtml && (
            <div css={styles.video}>
              <GenArtUrlIFrame url={animationUrl} />
            </div>
          )}
        </>
      ) : (
        <>
          <MediaQuery smallerThan="md" styles={{ display: "none" }}>
            <ResizedImage
              src={tokens[0].image ?? ""}
              fit="contain"
              styles={{
                image: { maxWidth: "100%" },
              }}
              onClick={expandImage}
              height={upscaleImage ? 1000 : 500}
              width="auto"
              style={{ filter: "blur(0)", zIndex: 1, cursor: "pointer" }}
            />
          </MediaQuery>
          <MediaQuery largerThan="md" styles={{ display: "none" }}>
            <ResizedImage
              src={tokens[0].image ?? ""}
              fit="scale-down"
              onClick={expandImage}
              fitSize={upscaleImage ? 1000 : 500}
              style={{ filter: "blur(0)", zIndex: 1, cursor: "pointer" }}
            />
          </MediaQuery>
        </>
      )}
    </Center>
  );
};
